/**
 * 学习乐园 · 知识连连看（game-lianliankan.js）
 * ============================================================
 * 与传统连连看的差异点（本栏目产品底线——反馈要即时且可解释）：
 * 配错必须说清「为什么不对」——关联型题材告诉你正确搭档是谁
 * （「3×4」的正确搭档是「12」），相同型告诉你两张各是什么。
 *
 * 架构（与五子棋/琴谱同口径）：零 vendor、零请求。
 * 题材数据不在本文件——由 lianliankan/pack-*.js 注册进全局 STLLK_PACKS
 * （footer 按 config/study.php 的 packs 字段按需加载，defer 文档序保证
 * 先于本文件执行）。引擎只认 pair id，与题材解耦：新增题材零改引擎。
 *
 * 简化红线（页内已如实说明）：一局两三分钟，不做中断续局恢复，刷新即
 * 重开；成绩与偏好只存本机 localStorage，不上传。
 *
 * 连通规则：经典口径——两点沿横竖线连通，转弯不超过 2 次，路径上除两端
 * 外不得有牌；棋盘外圈视为空，可贴边绕行。
 *
 * 玩法两档（默认简易局）：简易局配对正确即消，不看路线——有牌必有解，
 * 永不重排；标准局配对 + 连通，难度闸门、死局重排、连通提示只在标准局生效。
 *
 * 冒烟钩子：window.STLianliankan 暴露纯引擎函数（canLink / hasAnyLink /
 * countLinks / findAnyPair / findAnyPairLoose / redeal / dealSolvable /
 * buildDeck / missExplain），供冒烟跑确定性用例。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-lianliankan';
  var RECORD_KEY = 'study-lianliankan-record';
  var HINT_MS = 2500;
  var GONE_MS = 320;
  var BAD_MS = 420;

  /* ============ 引擎：牌堆与布局（纯函数） ============ */

  // 抽 count 对生成牌数组（每对两张，tile.mate = 正确搭档牌面，配错反馈用）
  function buildDeck(pairs, count) {
    var pool = pairs.slice();
    shuffle(pool);
    var n = Math.min(count, pool.length);
    var tiles = [];
    for (var i = 0; i < n; i++) {
      var p = pool[i];
      tiles.push({ pair: i, side: 'l', face: p.l, mate: p.r, note: p.note || '' });
      tiles.push({ pair: i, side: 'r', face: p.r, mate: p.l, note: p.note || '' });
    }
    return tiles;
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  // 牌铺进 rows×cols 网格（牌数不足时尾部留空格）；grid[r][c] = tile | null
  function dealGrid(tiles, rows, cols) {
    var order = shuffle(tiles.slice());
    var grid = [];
    for (var r = 0; r < rows; r++) {
      grid.push([]);
      for (var c = 0; c < cols; c++) {
        var idx = r * cols + c;
        grid[r].push(idx < order.length ? order[idx] : null);
      }
    }
    return grid;
  }

  /* ---- 连通判定：坐标可越界（外圈视为空），路径上不得有牌 ---- */

  function cellEmpty(grid, rows, cols, r, c) {
    if (r < 0 || r >= rows || c < 0 || c >= cols) return true;
    return !grid[r][c];
  }

  // 同行 r 开区间 (cA, cB) 内全空；行越界（外圈横线）视为通
  function rowClear(grid, rows, cols, r, cA, cB) {
    if (r < 0 || r >= rows) return true;
    var lo = Math.min(cA, cB) + 1, hi = Math.max(cA, cB) - 1, c;
    for (c = Math.max(lo, 0); c <= Math.min(hi, cols - 1); c++) {
      if (grid[r][c]) return false;
    }
    return true;
  }

  // 同列 c 开区间 (rA, rB) 内全空；列越界视为通
  function colClear(grid, rows, cols, c, rA, rB) {
    if (c < 0 || c >= cols) return true;
    var lo = Math.min(rA, rB) + 1, hi = Math.max(rA, rB) - 1, r;
    for (r = Math.max(lo, 0); r <= Math.min(hi, rows - 1); r++) {
      if (grid[r][c]) return false;
    }
    return true;
  }

  // 两格能否连通（0/1/2 次转弯；a、b 两格必须各有一张牌）
  function canLink(grid, rows, cols, a, b) {
    var r1 = a.r, c1 = a.c, r2 = b.r, c2 = b.c, r, c;

    // 同一格不构成连通（防御：正常调用不会传同一格，但 0 弯判定会把它当直通放行）
    if (r1 === r2 && c1 === c2) return false;

    // 0 弯：同行或同列直通
    if (r1 === r2 && rowClear(grid, rows, cols, r1, c1, c2)) return true;
    if (c1 === c2 && colClear(grid, rows, cols, c1, r1, r2)) return true;

    // 1 弯：矩形两角，拐点须为空
    if (cellEmpty(grid, rows, cols, r1, c2)
      && rowClear(grid, rows, cols, r1, c1, c2)
      && colClear(grid, rows, cols, c2, r1, r2)) return true;
    if (cellEmpty(grid, rows, cols, r2, c1)
      && rowClear(grid, rows, cols, r2, c1, c2)
      && colClear(grid, rows, cols, c1, r1, r2)) return true;

    // 2 弯：枚举中间横线 / 竖线（含外圈）
    for (r = -1; r <= rows; r++) {
      if (r === r1 || r === r2) continue;
      if (cellEmpty(grid, rows, cols, r, c1) && cellEmpty(grid, rows, cols, r, c2)
        && colClear(grid, rows, cols, c1, r1, r)
        && colClear(grid, rows, cols, c2, r2, r)
        && rowClear(grid, rows, cols, r, c1, c2)) return true;
    }
    for (c = -1; c <= cols; c++) {
      if (c === c1 || c === c2) continue;
      if (cellEmpty(grid, rows, cols, r1, c) && cellEmpty(grid, rows, cols, r2, c)
        && rowClear(grid, rows, cols, r1, c1, c)
        && rowClear(grid, rows, cols, r2, c2, c)
        && colClear(grid, rows, cols, c, r1, r2)) return true;
    }
    return false;
  }

  // 还在盘上的每对牌的位置（pair → 两格）
  function pairPositions(grid) {
    var map = {};
    for (var r = 0; r < grid.length; r++) {
      for (var c = 0; c < grid[r].length; c++) {
        var t = grid[r][c];
        if (!t) continue;
        (map[t.pair] = map[t.pair] || []).push({ r: r, c: c });
      }
    }
    return map;
  }

  // 盘上是否还有能连的一对（死局检测）
  function hasAnyLink(grid, rows, cols) {
    var map = pairPositions(grid), k;
    for (k in map) {
      if (map[k].length === 2 && canLink(grid, rows, cols, map[k][0], map[k][1])) return true;
    }
    return false;
  }

  // 盘上当前能连的对数（开局与重排共用的难度闸门计量）
  function countLinks(grid, rows, cols) {
    var map = pairPositions(grid), k, n = 0;
    for (k in map) {
      if (map[k].length === 2 && canLink(grid, rows, cols, map[k][0], map[k][1])) n++;
    }
    return n;
  }

  // 铺一个「能连的对数 ≥ cap」的盘面：开局与死局重排共用这道闸门。
  // 只保证 hasAnyLink（≥1 对）会出现「提示指的那对是唯一能连的，消掉
  // 立刻死局重排」的憋屈局——闸门提到 3（牌不足 3 对时封顶），消一对还
  // 剩两对兜着；凑不出逐级放水到 1，最终必返回有解盘面（真凑不出才 null）
  function dealSolvable(tiles, rows, cols, level) {
    var cap = Math.max(1, Math.min(level, tiles.length / 2));
    while (cap >= 1) {
      for (var i = 0; i < 60; i++) {
        var grid = dealGrid(tiles, rows, cols);
        if (countLinks(grid, rows, cols) >= cap) return grid;
      }
      cap--;
    }
    return null;
  }

  // 找一对能连的（标准局提示用）；返回两格或 null
  function findAnyPair(grid, rows, cols) {
    var map = pairPositions(grid), k;
    for (k in map) {
      if (map[k].length === 2 && canLink(grid, rows, cols, map[k][0], map[k][1])) {
        return [map[k][0], map[k][1]];
      }
    }
    return null;
  }

  // 盘上任意一对（简易局提示用：配对正确即消，不要求连通，死局概念不存在）
  function findAnyPairLoose(grid) {
    var map = pairPositions(grid), k;
    for (k in map) {
      if (map[k].length === 2) return [map[k][0], map[k][1]];
    }
    return null;
  }

  // 死局重排：剩余牌换位置重新铺，闸门同开局（≥3 对可连，凑不出逐级
  // 放水到 1）——重排后至少有两对兜着，治「重排完消一对又死局」的连环
  // 重排；只剩一两对时 cap 自动降级，最后一对盘面近乎全空必有解。
  // 真凑不出（实际不可达）返回 null，由调用方如实说明，绝不硬塞死局盘面
  function redeal(grid, rows, cols) {
    var tiles = [];
    for (var r = 0; r < rows; r++) {
      for (var c = 0; c < cols; c++) {
        if (grid[r][c]) tiles.push(grid[r][c]);
      }
    }
    return dealSolvable(tiles, rows, cols, 3);
  }

  /* ---- 配错反馈（栏目底线：说清为什么不对） ---- */

  function missExplain(a, b, kind) {
    if (a.pair === b.pair) {
      return '这两个是一对，但现在连不过去——两点之间被挡死了。先消别的牌，把路打开。';
    }
    if (kind === 'rel') {
      var tail = a.note ? '（' + a.note + '）' : '';
      return '配错了：「' + a.face + '」的正确搭档是「' + a.mate + '」' + tail;
    }
    return '这两个不是同一种：一个是「' + a.face + '」，一个是「' + b.face + '」';
  }

  // 消除成功时说什么（关联型顺手把完整成语/口诀亮出来——配上就是学到的一瞬）
  function pairExplain(a, b, kind) {
    if (kind === 'rel') {
      return '连上了：「' + a.face + '」连「' + b.face + '」' + (a.note ? '——' + a.note : '');
    }
    return '同款消除，连对 +1';
  }

  /* ============ UI：挂载与状态 ============ */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  var root = null;
  var ui = {};
  var S = {
    packId: '', soundOn: true, pack: null,
    mode: 'easy',         // 默认简易局；'std' 标准局（配对 + 连通）| 'easy'（配对即消）
    rows: 0, cols: 0,
    grid: null,           // [r][c] -> tile | null
    tilesLeft: 0,
    sel: null,            // 第一张选中的 {r, c}
    combo: 0, misses: 0, hints: 0, reshuffles: 0,
    elapsed: 0, timer: null,
    ended: false,
    hintCells: null,      // 提示高亮 [{r,c},{r,c}]
    hintTimer: null,      // 提示自动清除计时器 id
    resetArm: false,      // 重置成绩的两击确认
    resetTimer: null      // 确认态 3 秒复原计时器 id
  };

  /* ---- 题材选择器 ---- */

  function renderPackButtons() {
    var box = ui.packs;
    box.textContent = '';
    var ids = Object.keys(global.STLLK_PACKS || {});
    if (!ids.length) {
      box.appendChild(el('p', 'st-llk-none', '题材包没有加载出来，刷新一次试试。'));
      return;
    }
    for (var i = 0; i < ids.length; i++) {
      (function (id) {
        var p = global.STLLK_PACKS[id];
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-pack', id);
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', p.name));
        b.appendChild(el('small', 'st-llk-pack-hint', p.hint));
        b.addEventListener('click', function () {
          if (S.packId === id) return;
          newGame(id);   // 换题材 = 直接开新局（一局本来就两三分钟）
        });
        box.appendChild(b);
      })(ids[i]);
    }
  }

  function syncPackButtons() {
    var btns = ui.packs.querySelectorAll('[data-pack]');
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-pack') === S.packId;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 棋盘 ---- */

  // 牌面字号档：1 字最大、4 字以上最小；same 型 emoji 牌单独放大
  function faceClass(face, kind) {
    var n = face.length;
    var len = n <= 1 ? '1' : (n === 2 ? '2' : (n === 3 ? '3' : '4'));
    return 'st-llk-face st-llk-face--' + len + (kind === 'same' ? ' st-llk-face--emoji' : '');
  }

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-llk-cols', String(S.cols));
    ui.cells = [];
    for (var r = 0; r < S.rows; r++) {
      for (var c = 0; c < S.cols; c++) {
        (function (cr, cc) {
          var b = el('button', 'st-llk-tile');
          b.type = 'button';
          // aria-label 由 renderTiles 统一写（含牌面与瞬时态），这里不预置
          b.appendChild(el('span', 'st-llk-face'));
          b.addEventListener('click', function () { onTile(cr, cc); });
          board.appendChild(b);
          ui.cells.push(b);
        })(r, c);
      }
    }
    renderTiles();
  }

  function cellBtn(r, c) { return ui.cells[r * S.cols + c]; }

  function isHintCell(r, c) {
    if (!S.hintCells || S.hintCells.length !== 2) return false;
    return (S.hintCells[0].r === r && S.hintCells[0].c === c)
      || (S.hintCells[1].r === r && S.hintCells[1].c === c);
  }

  function renderTiles() {
    for (var r = 0; r < S.rows; r++) {
      for (var c = 0; c < S.cols; c++) {
        var btn = cellBtn(r, c);
        var t = S.grid ? S.grid[r][c] : null;
        var face = btn.firstChild;
        // 读屏可闻的牌面：只报行列等于让视障用户盲选，必须带上牌面文本与瞬时态
        var label = '第' + (r + 1) + '行第' + (c + 1) + '列';
        label += t ? '，' + t.face : '，空格';
        if (S.sel && S.sel.r === r && S.sel.c === c) label += '，已选中';
        else if (isHintCell(r, c)) label += '，提示指向';
        btn.setAttribute('aria-label', label);
        if (t) {
          face.className = faceClass(t.face, S.pack && S.pack.kind);
          face.textContent = t.face;
          // 格子里有牌就清掉一切瞬时/残留态：is-gone 不清会让重排进来的新牌 opacity:0 隐身
          btn.classList.remove('is-empty');
          btn.classList.remove('is-gone');
          btn.classList.remove('is-hit');
          btn.classList.remove('is-bad');
          btn.disabled = false;
        } else {
          face.textContent = '';
          btn.classList.add('is-empty');
          btn.disabled = true;
        }
        btn.classList.toggle('is-sel', !!S.sel && S.sel.r === r && S.sel.c === c);
        btn.classList.toggle('is-hint', isHintCell(r, c));
      }
    }
  }

  /* ---- 渲染：状态 / 反馈 / 成绩 / 按钮 ---- */

  function fmtTime(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒';
  }

  // 小数据格用的短格式：不满一分钟不显示「0 分」
  function fmtTimeShort(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m ? m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒' : s + ' 秒';
  }

  function renderStatus() {
    if (S.ended) return;   // 通关总结由 winGame 写入，不覆盖
    ui.status.className = 'st-status';
    ui.status.textContent = '剩 ' + (S.tilesLeft / 2) + ' 对 · ' + (S.mode === 'easy' ? '简易' : '标准')
      + ' · 用时 ' + fmtTime(S.elapsed)
      + ' · 连对 ' + S.combo
      + (S.reshuffles ? ' · 重排 ' + S.reshuffles : '');
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  // 成绩渲染成三个小数据格；无成绩时给弱化的空态文案（label 口径与 winGame 一致：
  // bestTime/bestMisses 是「最好用时那一局」的数，不是历史极值分开记）
  // 成绩键：按题材 + 玩法分开记（简易的快用时不应顶掉标准局的最好成绩）；
  // 标准局不带后缀——与历史数据天然兼容，老成绩自动归入标准局
  function recKey() { return S.packId + (S.mode === 'easy' ? ':easy' : ''); }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord()[recKey()];
    if (!rec || !rec.wins) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty', '这个题材还没通关过——通关一次，这里记下你的最好用时。'));
      return;
    }
    box.className = 'st-llk-record';
    box.appendChild(recordStat('通关', rec.wins + ' 次'));
    box.appendChild(recordStat('最好用时', fmtTimeShort(rec.bestTime)));
    box.appendChild(recordStat('那局配错', rec.bestMisses + ' 次'));
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderButtons() {
    ui.newBtn.disabled = false;
    ui.hintBtn.disabled = S.ended || !S.tilesLeft;
    // 两击确认的第一击之后按钮必须保持可点——disable 了第二击就永远点不到，
    // 确认态改用文案 + is-arm 样式标记，3 秒后自动复原
    ui.resetBtn.disabled = false;
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置这个题材的成绩';
  }

  function renderAll() {
    renderTiles();
    renderStatus();
    renderButtons();
  }

  /* ---- 音效（WebAudio 本地合成，无音频文件） ---- */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else if (kind === 'shuffle') seq = [[392, 0], [330, 0.08]];
      else seq = [[330, 0]];   // sel
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g);
        g.connect(actx.destination);
        o.start(t + seq[i][1]);
        o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* ---- 计时 ---- */

  function startTimer() {
    stopTimer();
    S.elapsed = 0;
    S.timer = setInterval(function () {
      if (S.ended) return;
      S.elapsed++;
      renderStatus();
    }, 1000);
  }

  function stopTimer() {
    if (S.timer) { clearInterval(S.timer); S.timer = null; }
  }

  /* ---- 交互：选牌与消除 ---- */

  function onTile(r, c) {
    if (S.ended) return;
    var t = S.grid[r][c];
    if (!t) return;
    clearHint();
    if (S.sel && S.sel.r === r && S.sel.c === c) {   // 再点自己 = 取消选中
      S.sel = null;
      renderAll();
      return;
    }
    if (!S.sel) {
      S.sel = { r: r, c: c };
      sfx('sel');
      renderAll();
      return;
    }
    var a = S.grid[S.sel.r][S.sel.c];
    var first = S.sel;
    S.sel = null;
    if (a.pair === t.pair) {
      // 简易局：配对正确即消，不查路线；标准局还要连得通（转弯 ≤2、不压牌）
      if (S.mode === 'easy' || canLink(S.grid, S.rows, S.cols, first, { r: r, c: c })) {
        removePair(first, { r: r, c: c }, a, t);
        return;
      }
      // 是一对但路径不通：如实说「挡住了」，不清连对（不是知识性错误）
      renderAll();
      renderNote(missExplain(a, t, packKind()), 'bad');
      flashBad([first, { r: r, c: c }]);
      sfx('bad');
      return;
    }
    S.combo = 0;
    S.misses++;
    renderAll();
    renderNote(missExplain(a, t, packKind()), 'bad');
    flashBad([first, { r: r, c: c }]);
    sfx('bad');
  }

  function packKind() { return S.pack && S.pack.kind === 'same' ? 'same' : 'rel'; }

  function removePair(p1, p2, ta, tb) {
    S.grid[p1.r][p1.c] = null;
    S.grid[p2.r][p2.c] = null;
    S.tilesLeft -= 2;
    S.combo++;
    markGone(p1, p2);
    sfx('ok');
    renderNote(pairExplain(ta, tb, packKind()), 'ok');
    if (!S.tilesLeft) {
      winGame();
    } else if (S.mode !== 'easy' && !hasAnyLink(S.grid, S.rows, S.cols)) {
      // 简易局不存在死局：配对正确即消，有牌必有解，无需重排
      autoRedeal();
    }
    renderAll();
  }

  // 消除动画：先落 is-hit（绿闪）+ is-gone 淡出，动画结束转 is-empty 占位（保持网格不塌）。
  // is-empty 必须条件落——autoRedeal 可能在 320ms 内把新牌重排进这两格，无脑加会让新牌隐身
  function markGone(p1, p2) {
    var b1 = cellBtn(p1.r, p1.c), b2 = cellBtn(p2.r, p2.c);
    b1.classList.add('is-gone'); b1.classList.add('is-hit');
    b2.classList.add('is-gone'); b2.classList.add('is-hit');
    setTimeout(function () {
      // GONE_MS 内可能已换题材/开新局：棋盘重建后旧按钮不在文档里，直接放弃，
      // 防止拿旧坐标去读新盘面（换小棋盘时 S.grid[p1.r] 直接越界）
      if (!b1.isConnected || !b2.isConnected) return;
      if (!S.grid[p1.r][p1.c]) { b1.classList.add('is-empty'); b1.classList.remove('is-hit'); }
      if (!S.grid[p2.r][p2.c]) { b2.classList.add('is-empty'); b2.classList.remove('is-hit'); }
    }, GONE_MS);
  }

  function flashBad(cells) {
    for (var i = 0; i < cells.length; i++) {
      cellBtn(cells[i].r, cells[i].c).classList.add('is-bad');
    }
    setTimeout(function () {
      for (var j = 0; j < cells.length; j++) {
        // 换题材/开新局后棋盘重建，旧按钮不在文档里就不动它
        var b = cellBtn(cells[j].r, cells[j].c);
        if (b && b.isConnected) b.classList.remove('is-bad');
      }
    }, BAD_MS);
  }

  function autoRedeal() {
    var next = redeal(S.grid, S.rows, S.cols);
    if (!next) {
      // 极小概率：怎么摆都无解。如实停局说明，不硬塞死局也不无限重排
      S.ended = true;
      stopTimer();
      S.sel = null;
      renderAll();
      ui.status.className = 'st-status is-over';
      ui.status.textContent = '这一局卡死了：重排多次都凑不出能连的一对（极小概率）。点「开始新一局」重开吧。';
      renderNote('重排后仍无解——点「开始新一局」重开。', 'warn');
      return;
    }
    S.grid = next;
    S.reshuffles++;
    S.sel = null;
    sfx('shuffle');
    renderNote('没有能连的了，剩下的牌已自动重排（第 ' + S.reshuffles + ' 次，如实计入）', 'warn');
  }

  /* ---- 提示：高亮一对能连的 ---- */

  function hint() {
    if (S.ended || !S.tilesLeft) return;
    var pair = S.mode === 'easy' ? findAnyPairLoose(S.grid) : findAnyPair(S.grid, S.rows, S.cols);
    if (!pair) return;
    S.hints++;
    S.hintCells = pair;
    renderTiles();
    renderNote(S.mode === 'easy'
      ? '简易局配对就能消——指的这一对直接点掉就行（第 ' + S.hints + ' 次提示）。'
      : '给你指了一对能连的（第 ' + S.hints + ' 次提示）——路线还是要自己找。', 'warn');
    if (S.hintTimer) clearTimeout(S.hintTimer);
    S.hintTimer = setTimeout(function () { S.hintTimer = null; clearHint(); }, HINT_MS);
  }

  function clearHint() {
    if (S.hintTimer) { clearTimeout(S.hintTimer); S.hintTimer = null; }
    if (!S.hintCells) return;
    S.hintCells = null;
    renderTiles();
  }

  /* ---- 通关与成绩（只存本机） ---- */

  function winGame() {
    S.ended = true;
    stopTimer();
    sfx('win');
    var rec = loadRecord();
    var cur = rec[recKey()];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) {
      cur = { plays: 0, wins: 0, bestTime: 0, bestMisses: 0 };
    }
    cur.plays++;
    cur.wins++;
    var isBest = !cur.bestTime || S.elapsed < cur.bestTime;
    if (isBest) {
      cur.bestTime = S.elapsed;
      cur.bestMisses = S.misses;
    }
    rec[recKey()] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '全部消除！' + (S.mode === 'easy' ? '简易' : '标准') + '玩法，用时 ' + fmtTime(S.elapsed) + '，配错 ' + S.misses
      + ' 次，提示 ' + S.hints + ' 次，重排 ' + S.reshuffles + ' 次。'
      + (isBest ? '这是这个题材的最好成绩。' : '（最好成绩 ' + fmtTime(cur.bestTime) + '）')
      + '点「开始新一局」再来一把。';
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      // 数组形式（脏数据）按无记录处理，避免 rec[packId] 读出数组元素
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  // 重置成绩：两击确认（不用 confirm——jsdom 与手机端体验都差）
  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try {
      var rec = loadRecord();
      delete rec[recKey()];
      localStorage.setItem(RECORD_KEY, JSON.stringify(rec));
    } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置「' + (S.pack ? S.pack.name : '') + (S.mode === 'easy' ? ' · 简易' : '') + '」的本机成绩');
  }

  /* ---- 偏好（题材/音效）存本机 ---- */

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, packId: S.packId, soundOn: S.soundOn, mode: S.mode }));
    } catch (e) { /* 存储不可用静默（无痕模式） */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      S.packId = typeof d.packId === 'string' ? d.packId : '';
      S.soundOn = d.soundOn !== false;
      S.mode = d.mode === 'std' ? 'std' : 'easy';   // 脏值/旧数据（无 mode 字段）兜底默认简易局
    } catch (e) { /* 忽略脏数据 */ }
  }

  /* ---- 开局 ---- */

  function newGame(packId) {
    var packs = global.STLLK_PACKS || {};
    // 首访（无本机偏好）或偏好里的题材已下架：兜底取注册序第一个，页面不能空着
    if (!packs[packId]) packId = Object.keys(packs)[0] || '';
    var pack = packs[packId];
    if (!pack) return;
    S.pack = pack;
    S.packId = packId;
    S.rows = pack.rows;
    S.cols = pack.cols;
    var deck = buildDeck(pack.pairs, Math.floor(S.rows * S.cols / 2));
    if (S.mode === 'easy') {
      // 简易局不查连通：任何摆法都有解，无需闸门
      S.grid = dealGrid(deck, S.rows, S.cols);
    } else {
      // 标准局开局走与重排同一道闸门：至少 3 对能连（对数不足 3 的迷你盘
      // 要求全部可连）——提示消一对后还剩两对兜着，不会走一步就触发自动
      // 重排；|| dealGrid 仅在理论不可达的 null 时兜底防白屏
      S.grid = dealSolvable(deck, S.rows, S.cols, 3) || dealGrid(deck, S.rows, S.cols);
    }
    S.tilesLeft = deck.length;
    S.sel = null;
    S.combo = 0;
    S.misses = 0;
    S.hints = 0;
    S.reshuffles = 0;
    S.ended = false;
    S.hintCells = null;
    if (S.hintTimer) { clearTimeout(S.hintTimer); S.hintTimer = null; }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    syncPackButtons();
    buildBoard();
    startTimer();
    renderAll();
    renderRecord();
    renderNote(S.mode === 'easy'
      ? '「' + pack.name + '」简易局：点一对正确搭档就消，不用管路线。'
      : '「' + pack.name + '」标准局：先找一对正确搭档，再看路连不连得通。', '');
    save();
  }

  /* ---- 音效选择器（口径同五子棋 bindPicker） ---- */

  function bindSoundPicker() {
    var box = root.querySelector('[data-st-sound-picker]') || root;
    var btns = box.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 玩法选择器（简易/标准，口径同音效；切换 = 开新局，同换题材） ---- */

  function bindModePicker() {
    var btns = root.querySelectorAll('[data-st-mode]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          var m = b.getAttribute('data-st-mode') === 'easy' ? 'easy' : 'std';
          if (S.mode === m) return;
          S.mode = m;
          syncModeButtons();
          save();
          newGame(S.packId);
        });
      })(btns[i]);
    }
    syncModeButtons();
  }

  function syncModeButtons() {
    var btns = root.querySelectorAll('[data-st-mode]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-mode') === 'easy') === (S.mode === 'easy');
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- toast（口径同五子棋 st-saved） ---- */

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.packs = root.querySelector('[data-st-llk-packs]');
    ui.board = root.querySelector('[data-st-llk-board]');
    ui.status = root.querySelector('[data-st-llk-status]');
    ui.note = root.querySelector('[data-st-llk-note]');
    ui.record = root.querySelector('[data-st-llk-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.hintBtn = root.querySelector('[data-st-hint]');
    ui.resetBtn = root.querySelector('[data-st-reset]');

    restorePrefs();
    renderPackButtons();
    bindSoundPicker();
    bindModePicker();

    ui.newBtn.addEventListener('click', function () { newGame(S.packId); });
    ui.hintBtn.addEventListener('click', hint);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(S.packId);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STLianliankan = {
    buildDeck: buildDeck,
    dealGrid: dealGrid,
    shuffle: shuffle,
    canLink: canLink,
    hasAnyLink: hasAnyLink,
    countLinks: countLinks,
    findAnyPair: findAnyPair,
    findAnyPairLoose: findAnyPairLoose,
    redeal: redeal,
    dealSolvable: dealSolvable,
    missExplain: missExplain,
    pairExplain: pairExplain
  };
})(typeof window !== 'undefined' ? window : globalThis);
